// Library page for browsing captured frames across all videos
import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { 
  Image, 
  Film, 
  Search, 
  Download, 
  Trash2, 
  ArrowRight, 
  SortAsc,
  SortDesc,
  CheckSquare,
  Filter,
  X,
  FileImage
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useFrames } from '@/features/frames/use-frames'
import { CapturedFrame, framesStorageService } from '@/features/frames/frames-store'
import { StoredVideo, getUserVideos } from '@/features/videos/videos-store'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { formatFileSize } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh'
import { PullToRefreshIndicator } from '@/components/PullToRefreshIndicator'
import { LOCAL_LIBRARY_ID } from '@/lib/browser-persistence'

interface FramesLibraryPageProps {
  embedded?: boolean
}

export function FramesLibraryPage({ embedded = false }: FramesLibraryPageProps) {
  const navigate = useNavigate()
  const [videos, setVideos] = useState<StoredVideo[]>([])
  const [selectedVideoFilter, setSelectedVideoFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'frameNumber' | 'captureDate' | 'videoName'>('captureDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [stats, setStats] = useState<{ count: number; totalSize: number } | null>(null)

  // Pull-to-refresh setup
  const containerRef = useRef<HTMLDivElement>(null)

  const {
    frames,
    isLoading,
    selection,
    actions,
    updateFilter
  } = useFrames({
    userId: LOCAL_LIBRARY_ID,
    autoLoad: true
  })

  const handleRefresh = useCallback(async () => {
    await actions.refresh()
  }, [actions])
  
  const { pullDistance, isRefreshing } = usePullToRefresh(containerRef, {
    onRefresh: handleRefresh,
    enabled: !isLoading,
  })

  // Load videos for filter dropdown
  useEffect(() => {
    getUserVideos(LOCAL_LIBRARY_ID).then(setVideos)
  }, [])

  // Load stats
  useEffect(() => {
    framesStorageService.getFrameStorageStats(LOCAL_LIBRARY_ID).then(setStats)
  }, [frames])

  // Apply filters
  useEffect(() => {
    updateFilter({
      videoId: selectedVideoFilter === 'all' ? undefined : selectedVideoFilter,
      searchTerm: searchTerm || undefined,
      sortBy,
      sortOrder
    })
  }, [selectedVideoFilter, searchTerm, sortBy, sortOrder, updateFilter])

  const handleFrameClick = (frame: CapturedFrame) => {
    // Navigate to extractor with the video and highlight the frame
    navigate(`/?view=studio&video=${frame.videoId}&frame=${frame.frameNumber}`)
  }

  const handleDeleteSelected = async () => {
    if (selection.selectedCount === 0) return
    setShowDeleteDialog(true)
  }

  const confirmDelete = async () => {
    setIsDeleting(true)
    try {
      await actions.deleteSelected()
      toast.success(`Deleted ${selection.selectedCount} frame(s)`)
      setShowDeleteDialog(false)
    } catch {
      toast.error('Failed to delete frames')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDownloadSelected = async () => {
    if (selection.selectedCount === 0) return
    try {
      await actions.downloadSelected()
      toast.success(`Downloaded ${selection.selectedCount} frame(s)`)
    } catch {
      toast.error('Failed to download frames')
    }
  }

  // Group frames by video for potential future use
  const _framesByVideo = useMemo(() => {
    const grouped = new Map<string, CapturedFrame[]>()
    frames.forEach(frame => {
      const existing = grouped.get(frame.videoId) || []
      grouped.set(frame.videoId, [...existing, frame])
    })
    return grouped
  }, [frames])

  const hasActiveFilters = selectedVideoFilter !== 'all' || searchTerm

  return (
    <div 
      ref={containerRef}
      className={embedded ? '' : 'container mx-auto px-4 py-6 md:py-8 pb-20 md:pb-8 overflow-auto'}
    >
      {/* Pull-to-refresh indicator - mobile only */}
      <PullToRefreshIndicator 
        pullDistance={pullDistance} 
        isRefreshing={isRefreshing} 
      />
      
      {!embedded && (
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Image className="h-6 w-6 md:h-8 md:w-8" />
            Frame Captures
          </h1>
          {stats && (
            <p className="text-muted-foreground text-sm mt-1">
              {stats.count} frames • {formatFileSize(stats.totalSize)} total
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/?view=library&library=videos">
              <Film className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Videos</span>
            </Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/?view=studio">
              <span className="hidden sm:inline">Capture Frames</span>
              <span className="sm:hidden">Capture</span>
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
      )}

      {/* Toolbar */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search frames..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>

            {/* Video filter */}
            <Select value={selectedVideoFilter} onValueChange={setSelectedVideoFilter}>
              <SelectTrigger className="w-[200px]">
                <Film className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Videos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Videos</SelectItem>
                {videos.map(video => (
                  <SelectItem key={video.id} value={video.id}>
                    {video.name.length > 25 ? video.name.slice(0, 25) + '...' : video.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="captureDate">Capture Date</SelectItem>
                <SelectItem value="frameNumber">Frame Number</SelectItem>
                <SelectItem value="videoName">Video Name</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'desc' ? <SortDesc className="h-4 w-4" /> : <SortAsc className="h-4 w-4" />}
            </Button>

            {/* Clear filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('')
                  setSelectedVideoFilter('all')
                }}
              >
                <Filter className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Selection Toolbar - slides up when items selected (PRD 10.5.6) */}
      {selection.selectedCount > 0 && (
        <div className="mb-4 py-2 px-4 rounded-lg border border-primary/50 bg-primary/5 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {selection.selectedCount} selected
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={selection.selectAll}
              className="h-7 text-xs text-muted-foreground"
            >
              <CheckSquare className="h-3 w-3 mr-1" />
              All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={selection.selectNone}
              className="h-7 text-xs text-muted-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                const selectedFrames = frames.filter(f => selection.selectedIds.has(f.id))
                const frameData = selectedFrames.map(f => ({
                  id: f.id,
                  imageData: f.imageData,
                  width: f.width || 640,
                  height: f.height || 360,
                }))
                const encoded = btoa(JSON.stringify(frameData))
                navigate(`/?view=gif&frames=${encoded}&mode=add`)
              }}
            >
              <FileImage className="h-3 w-3 mr-1" />
              Add to GIF
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleDownloadSelected}>
              <Download className="h-3 w-3 mr-1" />
              Download ZIP
            </Button>
            <Button 
              variant="destructive" 
              size="sm"
              className="h-7 text-xs"
              onClick={handleDeleteSelected}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[...Array(12)].map((_, i) => (
            <Skeleton key={i} className="aspect-video rounded-lg" />
          ))}
        </div>
      ) : frames.length === 0 ? (
        <Card className="py-16">
          <CardContent className="flex flex-col items-center justify-center text-center">
            {hasActiveFilters ? (
              <>
                <Filter className="h-16 w-16 text-muted-foreground mb-4" />
                <h2 className="text-2xl font-semibold mb-2">No frames match your filters</h2>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your search or filter criteria
                </p>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('')
                    setSelectedVideoFilter('all')
                  }}
                >
                  Clear Filters
                </Button>
              </>
            ) : (
              <>
                <Image className="h-16 w-16 text-muted-foreground mb-4" />
                <h2 className="text-2xl font-semibold mb-2">No frames captured yet</h2>
                <p className="text-muted-foreground mb-6 max-w-md">
                  Open a video in the extractor and use the camera button to capture frames.
                </p>
                <Button asChild>
                  <Link to="/?view=studio">
                    Open Frame Extractor
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        // Masonry-style layout using CSS columns
        <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 xl:columns-6 gap-4 space-y-4">
          {frames.map((frame) => {
            const isSelected = selection.isSelected(frame.id)
            
            return (
              <div
                key={frame.id}
                className={cn(
                  'group relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer hover:shadow-lg break-inside-avoid',
                  isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent hover:border-muted-foreground/30'
                )}
                onClick={() => handleFrameClick(frame)}
              >
                {/* Thumbnail - natural aspect ratio */}
                <div className="bg-muted">
                  <img
                    src={frame.thumbnailData || frame.imageData}
                    alt={`Frame ${frame.frameNumber}`}
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>

                {/* Selection checkbox */}
                <div 
                  className="absolute top-2 left-2 z-10"
                  onClick={(e) => {
                    e.stopPropagation()
                    selection.toggleSelection(frame.id)
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    className={cn(
                      'h-5 w-5 border-2 bg-background/80 backdrop-blur-sm',
                      isSelected && 'border-blue-500'
                    )}
                  />
                </div>

                {/* Info overlay */}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-8">
                  <div className="text-white text-xs">
                    <div className="font-medium truncate">#{frame.frameNumber}</div>
                    <div className="opacity-75 truncate text-[10px]">{frame.videoName}</div>
                  </div>
                </div>

                {/* Hover actions */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => {
                      e.stopPropagation()
                      actions.downloadFrame(frame)
                    }}
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selection.selectedCount} Frame{selection.selectedCount !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected frames. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
